import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";

const HERO_IMG =
    "https://images.unsplash.com/photo-1651050089406-1df57d4a627d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Home() {
    const { t } = useI18n();
    const [newArrivals, setNewArrivals] = useState([]);
    const [bestsellers, setBestsellers] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [na, bs] = await Promise.all([
                    api.get("/products", { params: { new_arrival: true } }),
                    api.get("/products", { params: { bestseller: true } }),
                ]);
                setNewArrivals(na.data.slice(0, 4));
                setBestsellers(bs.data.slice(0, 4));
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    return (
        <div data-testid="home-page">
            <Seo title={null} image={HERO_IMG} />

            <section className="relative min-h-[85vh] w-full overflow-hidden">
                <img
                    src={HERO_IMG}
                    alt="Make Rizk Streetwear"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50" />
                <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 min-h-[85vh] flex flex-col justify-end pb-16 sm:pb-24">
                    <p className="text-xs uppercase tracking-[0.4em] text-white/70 mb-6">
                        {t("hero.eyebrow")}
                    </p>
                    <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.9] max-w-4xl">
                        {t("hero.title_1")}<br />
                        {t("hero.title_2")}<br />
                        <span className="text-white/60">{t("hero.title_3")}</span>
                    </h1>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <Link
                            to="/collection"
                            className="group inline-flex items-center justify-center gap-3 px-8 h-14 bg-white text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-white/90 transition-colors"
                            data-testid="hero-cta-collection"
                        >
                            {t("hero.cta_collection")}
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform rtl:rotate-180 rtl:group-hover:-translate-x-1" />
                        </Link>
                        <Link
                            to="/collection?category=cap"
                            className="inline-flex items-center justify-center gap-3 px-8 h-14 border border-white/30 text-white text-xs uppercase tracking-[0.25em] font-bold hover:bg-white hover:text-black transition-colors"
                            data-testid="hero-cta-caps"
                        >
                            {t("hero.cta_caps")}
                        </Link>
                    </div>
                </div>
            </section>

            <section className="border-y border-white/10 bg-[#050505] overflow-hidden py-5">
                <div className="flex animate-marquee whitespace-nowrap gap-16 text-xs uppercase tracking-[0.4em] text-white/50 font-semibold">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-16 flex-shrink-0">
                            <span>{t("marquee.delivery")}</span>
                            <span>·</span>
                            <span>{t("marquee.cod")}</span>
                            <span>·</span>
                            <span>{t("marquee.returns")}</span>
                            <span>·</span>
                            <span>{t("marquee.limited")}</span>
                            <span>·</span>
                        </div>
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 sm:py-28" data-testid="new-arrivals-section">
                <div className="flex items-end justify-between mb-10 gap-6">
                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">
                            {t("section.new_eyebrow")}
                        </p>
                        <h2 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tighter">
                            {t("section.new_title")}
                        </h2>
                    </div>
                    <Link
                        to="/collection"
                        className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold link-underline"
                        data-testid="new-arrivals-view-all"
                    >
                        {t("section.view_all")} <ArrowRight size={14} className="rtl:rotate-180" />
                    </Link>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {newArrivals.map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} />
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-[1400px] px-5 sm:px-8 pb-20 sm:pb-28">
                <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                    {[
                        {
                            title: t("nav.tshirts"),
                            sub: t("section.tshirts_sub"),
                            img: "https://images.unsplash.com/photo-1616006897093-5e4635c0de35?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
                            to: "/collection?category=tshirt",
                            tid: "cat-tshirts",
                        },
                        {
                            title: t("nav.caps"),
                            sub: t("section.caps_sub"),
                            img: "https://images.unsplash.com/photo-1651050030678-3c9d207d8a41?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
                            to: "/collection?category=cap",
                            tid: "cat-caps",
                        },
                    ].map((cat) => (
                        <Link
                            key={cat.tid}
                            to={cat.to}
                            className="group relative aspect-[4/5] md:aspect-[5/6] overflow-hidden bg-[#0a0a0a]"
                            data-testid={cat.tid}
                        >
                            <img
                                src={cat.img}
                                alt={cat.title}
                                className="product-image w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
                                <p className="text-xs uppercase tracking-[0.3em] text-white/70 mb-2">
                                    {cat.sub}
                                </p>
                                <h3 className="font-display text-4xl sm:text-6xl font-black uppercase tracking-tighter">
                                    {cat.title}
                                </h3>
                                <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold">
                                    {t("section.explore")}{" "}
                                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform rtl:rotate-180 rtl:group-hover:-translate-x-1" />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-[1400px] px-5 sm:px-8 pb-20 sm:pb-28" data-testid="bestsellers-section">
                <div className="flex items-end justify-between mb-10 gap-6">
                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">
                            {t("section.best_eyebrow")}
                        </p>
                        <h2 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tighter">
                            {t("section.best_title")}
                        </h2>
                    </div>
                    <Link
                        to="/collection"
                        className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold link-underline"
                        data-testid="bestsellers-view-all"
                    >
                        {t("section.view_all")} <ArrowRight size={14} className="rtl:rotate-180" />
                    </Link>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {bestsellers.map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} />
                    ))}
                </div>
            </section>

            <section className="border-t border-white/10 bg-[#0a0a0a]">
                <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
                    {[
                        { icon: Truck, title: t("trust.delivery_title"), desc: t("trust.delivery_desc") },
                        { icon: ShieldCheck, title: t("trust.cod_title"), desc: t("trust.cod_desc") },
                        { icon: RotateCcw, title: t("trust.returns_title"), desc: t("trust.returns_desc") },
                    ].map((f) => {
                        const Icon = f.icon;
                        return (
                            <div key={f.title} className="flex items-start gap-4">
                                <Icon size={26} strokeWidth={1.2} className="mt-1 flex-shrink-0" />
                                <div>
                                    <p className="font-display text-lg font-bold uppercase tracking-tight">
                                        {f.title}
                                    </p>
                                    <p className="text-sm text-white/60 mt-1">{f.desc}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
